import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getCategoryLimit } from '@common/utils/widgetConfig';
import { axisTooltipFormatter } from '@common/utils/chartTooltipFormatter';
import { truncateLabel } from '@common/utils/chartUtils';
import ChartWidgetContainer from '@common/components/ChartWidgetContainer';

function BarChartWidget({ widget }) {
  const { t } = useTranslation();
  const { data, config } = widget;

  const otherLabel = t('dashboard.widgets.other');

  const option = useMemo(() => {
    if (!data) return null;

    const CATEGORY_LIMIT = getCategoryLimit(config);
    let processedCategories = data.categories || [];
    let processedValues = data.values || [];
    let processedCategoryColors = data.categoryColors || null;

    if (CATEGORY_LIMIT && processedCategories.length > CATEGORY_LIMIT) {
      const topCategories = processedCategories.slice(0, CATEGORY_LIMIT);
      const topValues = processedValues.slice(0, CATEGORY_LIMIT);
      const remainingValues = processedValues.slice(CATEGORY_LIMIT);
      const otherValue = remainingValues.reduce((sum, val) => sum + val, 0);

      if (otherValue > 0) {
        processedCategories = [...topCategories, otherLabel];
        processedValues = [...topValues, otherValue];
        if (processedCategoryColors) {
          processedCategoryColors = [...processedCategoryColors.slice(0, CATEGORY_LIMIT), null];
        }
      } else {
        processedCategories = topCategories;
        processedValues = topValues;
        if (processedCategoryColors) {
          processedCategoryColors = processedCategoryColors.slice(0, CATEGORY_LIMIT);
        }
      }
    }

    const seriesData = processedCategoryColors
      ? processedValues.map((v, i) => {
          const catColor = processedCategoryColors[i];
          return catColor ? { value: v, itemStyle: { color: catColor } } : v;
        })
      : processedValues;

    return {
      tooltip: {
        trigger: 'axis',
        confine: true,
        appendToBody: true,
        axisPointer: {
          type: 'shadow',
        },
        formatter: axisTooltipFormatter,
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: processedCategories,
        axisLabel: {
          fontSize: 11,
          rotate: processedCategories && processedCategories.some((cat) => cat.length > 10) ? 45 : 0,
          formatter: (value) => truncateLabel(value),
        },
        tooltip: {
          show: true,
          formatter: (params) => params.value,
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          fontSize: 11,
        },
      },
      series: [
        {
          name: widget.title,
          type: 'bar',
          data: seriesData,
          itemStyle: {
            color: data.color || '#3498db',
          },
        },
      ],
    };
  }, [data, config, widget.title, otherLabel]);

  return <ChartWidgetContainer widget={widget} option={option} />;
}

export default BarChartWidget;
